/**
 * BOQ Summary Table
 * Displays the materials/labor/total breakdown per category with grand totals.
 * Supports unified view showing actual spend from all sources and variance.
 */

import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box, Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { brand, status as statusColors } from '../../theme/colors';

const fmt = (n) => {
  const val = parseFloat(n || 0);
  return val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * @param {Object} props
 * @param {Array} props.categories - from getDocument (basic) or getUnifiedSummary (integrated)
 * @param {Object} props.summary - summary totals
 * @param {boolean} props.unified - if true, show actual/variance columns from unified data
 */
export default function BOQSummaryTable({ categories = [], summary = {}, unified = false }) {
  const { total_materials = 0, total_labor = 0, grand_total = 0 } = summary;

  // Check if we have unified data (categories from getUnifiedSummary have .estimated/.actual)
  const hasUnifiedData = unified && categories.length > 0 && categories[0].estimated;

  const headerStyle = { color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' };

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderColor: brand.primaryLight + '30' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: brand.primaryDeep }}>
            <TableCell sx={headerStyle}>Summary</TableCell>
            <TableCell align="right" sx={headerStyle}>Materials ($)</TableCell>
            <TableCell align="right" sx={headerStyle}>Labour ($)</TableCell>
            <TableCell align="right" sx={headerStyle}>Estimated ($)</TableCell>
            {hasUnifiedData && (
              <>
                <TableCell align="right" sx={headerStyle}>Actual Spend ($)</TableCell>
                <TableCell align="right" sx={headerStyle}>Variance ($)</TableCell>
              </>
            )}
            <TableCell align="right" sx={headerStyle}>Progress</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {categories.map((cat, idx) => {
            const est = hasUnifiedData ? cat.estimated : null;
            const act = hasUnifiedData ? cat.actual : null;
            const vari = hasUnifiedData ? cat.variance : null;
            const materials = est ? est.materials : (cat.materials_subtotal || 0);
            const labor = est ? est.labor : (cat.labor_cost || 0);
            const total = est ? est.total : (cat.category_total || 0);
            const progress = cat.purchase_progress || 0;
            const laborPaid = act ? act.labor_paid : (cat.labor_paid || false);
            const laborPaidToDate = act ? act.labor_paid_to_date : (cat.labor_paid_to_date || 0);
            const laborBalance = act ? act.labor_balance : (cat.labor_balance ?? (labor - laborPaidToDate));

            return (
              <TableRow key={cat.category_id || cat.id || idx} sx={{ '&:nth-of-type(even)': { backgroundColor: 'rgba(13, 148, 136, 0.03)' } }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem', textTransform: 'uppercase' }}>
                  {cat.category_name || cat.name}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {fmt(materials)}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    {fmt(labor)}
                    {laborPaid && (
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                        label="Paid"
                        size="small"
                        sx={{ height: 18, fontSize: '0.6rem', bgcolor: statusColors.successBg, color: statusColors.success, fontWeight: 700, ml: 0.5 }}
                      />
                    )}
                  </Box>
                  {(laborPaidToDate > 0 || laborBalance !== labor) && (
                    <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                      paid {fmt(laborPaidToDate)} · owing {fmt(Math.max(laborBalance, 0))}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {fmt(total)}
                </TableCell>

                {hasUnifiedData && (
                  <>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 600 }}>
                      {fmt(act.total)}
                      {act.materials_from_orders > 0 && (
                        <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                          {fmt(act.materials_manual)} manual + {fmt(act.materials_from_orders)} orders
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 600,
                      color: vari.materials < 0 ? statusColors.error : vari.materials > 0 ? statusColors.success : 'inherit'
                    }}>
                      {vari.materials >= 0 ? '' : '-'}{fmt(Math.abs(vari.materials))}
                      {vari.percentage !== 0 && (
                        <Typography variant="caption" display="block" sx={{
                          fontSize: '0.6rem',
                          color: vari.percentage > 0 ? statusColors.error : statusColors.success
                        }}>
                          {vari.percentage > 0 ? '▲' : '▼'}{Math.abs(vari.percentage).toFixed(1)}%
                        </Typography>
                      )}
                    </TableCell>
                  </>
                )}

                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                    <Box sx={{ width: 60, height: 6, borderRadius: 3, bgcolor: '#e5e7eb', overflow: 'hidden' }}>
                      <Box sx={{
                        width: `${Math.min(progress, 100)}%`,
                        height: '100%',
                        borderRadius: 3,
                        bgcolor: progress >= 100 ? statusColors.success : brand.primary,
                        transition: 'width 0.3s ease'
                      }} />
                    </Box>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', minWidth: 32 }}>
                      {Math.round(progress)}%
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}

          {/* Totals row */}
          <TableRow sx={{ background: brand.gradient }}>
            <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', textTransform: 'uppercase' }}>Totals</TableCell>
            <TableCell align="right" sx={{ color: '#fff', fontWeight: 700, fontFamily: 'monospace', fontSize: '0.9rem' }}>
              {fmt(hasUnifiedData ? summary.estimated_materials : total_materials)}
            </TableCell>
            <TableCell align="right" sx={{ color: '#fff', fontWeight: 700, fontFamily: 'monospace', fontSize: '0.9rem' }}>
              {fmt(hasUnifiedData ? summary.estimated_labor : total_labor)}
            </TableCell>
            <TableCell align="right" sx={{ color: '#fff', fontWeight: 700, fontFamily: 'monospace', fontSize: '0.9rem' }}>
              {fmt(hasUnifiedData ? summary.estimated_total : grand_total)}
            </TableCell>
            {hasUnifiedData && (
              <>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 700, fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  {fmt(summary.actual_total)}
                  {summary.actual_labor > 0 && (
                    <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', color: '#fff', opacity: 0.85 }}>
                      labour paid {fmt(summary.actual_labor)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 700, fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  {fmt(summary.budget_remaining)}
                </TableCell>
              </>
            )}
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
